import { useTranslation } from "react-i18next";
import type { Widget } from "../../../widgets/types";
import { laterDelivery } from "./resolve";
import type { DeviceActions } from "./useDeviceActions";

/** The keys ("<device>/<key>") a widget's controls send under. */
export function controlKeys(w: Widget): string[] {
  return (w.bindings ?? []).flatMap((b) => {
    const k = b.command ?? b.setting;
    return k ? [`${b.device}/${k}`] : [];
  });
}

/**
 * What the widget does not say about its last command (WEB-CMD-02, -03): when a sleeping device will get it, with
 * Cancel; the device's error when it failed; and a refused send in words. The widget itself shows "Waiting".
 */
export function ActionStatus({ widget, actions, now }: { widget: Widget; actions: DeviceActions; now: Date }) {
  const { t, i18n } = useTranslation();
  if (widget.type === "scene_button") return <SceneStatus widget={widget} actions={actions} />;
  const lines = controlKeys(widget).flatMap((k) => {
    const c = actions.commands[k];
    const error = actions.errors[k];
    const later = c?.state === "queued" ? laterDelivery(c.expected_delivery_at, now) : undefined;
    const out = [];
    if (later)
      out.push(
        <p key={`${k}-later`} className="flex flex-wrap items-center gap-2 text-xs text-gray-800">
          {t("controls.later", {
            time: later.toLocaleTimeString(i18n.language, { hour: "2-digit", minute: "2-digit" }),
          })}
          <button
            type="button"
            onClick={() => void actions.cancel(k)}
            className="min-h-6 rounded px-2 py-1 font-medium text-brand underline-offset-2 hover:underline"
          >
            {t("controls.cancel")}
          </button>
        </p>,
      );
    if (c?.state === "failed" && c.result?.err)
      out.push(
        <p key={`${k}-err`} className="text-xs text-red-800">
          {t("controls.deviceSaid", { error: c.result.err })}
        </p>,
      );
    if (c?.state === "expired")
      out.push(
        <p key={`${k}-exp`} className="text-xs text-red-800">
          {t("controls.expired")}
        </p>,
      );
    if (error)
      out.push(
        <p key={`${k}-send`} role="alert" className="text-xs text-red-800">
          {error}
        </p>,
      );
    return out;
  });
  return lines.length ? <div className="px-1">{lines}</div> : null;
}

/**
 * A scene button's run (WEB-SCN-01 to -03): "Starting…" while the API is asked, "Started" for a few seconds after, a
 * refusal in words; and "Scene not found" when its automation is gone, or that scenes are not available yet.
 */
function SceneStatus({ widget, actions }: { widget: Widget; actions: DeviceActions }) {
  const { t } = useTranslation();
  const { scenes } = actions;
  const id = widget.automation ?? "";
  const status = scenes.status[id];
  const error = scenes.errors[id];
  const missing = scenes.list !== undefined && !scenes.list[id];
  return (
    <div className="px-1">
      <p role="status" className="text-xs text-gray-800">
        {status === "starting" ? t("scenes.starting") : status === "started" ? t("scenes.started") : ""}
      </p>
      {error ? (
        <p role="alert" className="text-xs text-red-800">
          {error}
        </p>
      ) : scenes.unavailable ? (
        <p className="text-xs text-gray-800">{t("scenes.notYet")}</p>
      ) : missing ? (
        <p className="text-xs text-red-800">{t("scenes.notFound")}</p>
      ) : null}
    </div>
  );
}
